import { ExecutionOutcome, ShelfState } from "interfaces";

/**
 * Recarga de Planos — mapa ÚNICO de status e desfecho (spec 036, RN04).
 *
 * Antes existiam dois: `list.tsx` decidia a cor da Tag do processo e `show.tsx` mantinha o mapa de
 * `outcome`. Rótulo e cor de status são vocabulário do produto, não decisão de tela.
 */

/** Tom semântico do processo. Mapeado para token de cor em um único lugar por superfície. */
export type PlanReloadTone = "running" | "success" | "warning" | "error";

export interface PlanReloadStatus {
  label: string;
  /** Cor da `Tag` do antd (nome de preset, não hex). */
  color: string;
  tone: PlanReloadTone;
}

/**
 * `last_status` é gravado pelo Plan.Producer/Admin.Api em minúsculas; a UI é pt-BR em Title Case.
 * Status desconhecido cai no rótulo cru (nunca em branco) — o backend pode ganhar estados novos
 * antes da tela.
 */
const PROCESS_STATUS: Record<string, PlanReloadStatus> = {
  enviado: { label: "Enviado", color: "default", tone: "running" },
  recebido: { label: "Recebido", color: "processing", tone: "running" },
  processando: { label: "Processando", color: "processing", tone: "running" },
  executando: { label: "Executando", color: "processing", tone: "running" },
  finalizado: { label: "Concluído", color: "green", tone: "success" },
};

/**
 * Rótulo de um processo já finalizado que não coletou um único plano (spec 033, RN12): nunca
 * "Concluído" em verde, porque uma recarga sem planos não é um sucesso.
 */
export const PROCESS_STATUS_NO_PLANS: PlanReloadStatus = {
  label: "Concluído sem planos",
  color: "warning",
  tone: "warning",
};

export const processStatus = (
  lastStatus?: string,
  noPlans = false
): PlanReloadStatus => {
  if (noPlans) return PROCESS_STATUS_NO_PLANS;
  if (!lastStatus) return { label: "-", color: "default", tone: "running" };
  return (
    PROCESS_STATUS[lastStatus] ?? {
      label: lastStatus,
      color: "default",
      tone: "running",
    }
  );
};

/** Rótulo humano do processo em andamento, usado na pílula do detalhe. */
export const PROCESS_RUNNING_LABEL = "Em andamento";

export interface PlanReloadOutcome {
  label: string;
  color: string;
  tooltip: string;
}

/**
 * Desfecho por filial. "Vazio" e "Falha" são sinais DISTINTOS (spec 033, RN11): o SIGO responder
 * com sucesso e sem planos não é falha. O que ficou na prateleira NÃO é dito aqui — é o
 * `SHELF_STATE` (spec 062): desde então, filial sem planos válidos é zerada (fail-closed).
 */
export const OUTCOME: Record<ExecutionOutcome, PlanReloadOutcome> = {
  success: {
    label: "Sucesso",
    color: "success",
    tooltip: "Planos coletados e aplicados.",
  },
  partial: {
    label: "Parcial",
    color: "warning",
    tooltip: "Finalizou, mas houve erros em parte dos planos.",
  },
  empty: {
    label: "Vazio",
    color: "gold",
    tooltip:
      "O SIGO respondeu sem nenhum plano válido. Não é falha. Veja o estado da prateleira ao lado.",
  },
  error: {
    label: "Falha",
    color: "error",
    tooltip:
      "A recarga desta filial falhou. Veja o estado da prateleira ao lado e o motivo da falha.",
  },
};

export interface PlanReloadShelfState {
  label: string;
  /** Cor da `Tag` do antd (nome de preset, não hex). */
  color: string;
  tooltip: string;
}

/**
 * Estado da prateleira da filial depois da recarga (spec 062). Só os estados que pedem ação têm
 * tag: `replaced` é o normal e não polui a linha. Os valores são contrato com o Plan.Producer
 * (`SHELF_*` em `reload_plans.py`).
 */
export const SHELF_STATE: Partial<Record<ShelfState, PlanReloadShelfState>> = {
  cleared: {
    label: "Prateleira zerada",
    color: "error",
    tooltip:
      "Nenhum plano válido nesta recarga: a filial foi zerada e está sem planos à venda até a próxima recarga com sucesso.",
  },
  stale: {
    label: "Planos antigos no ar",
    color: "error",
    tooltip:
      "Nem a troca nem a limpeza puderam ser gravadas. A filial continua vendendo planos não confirmados por esta recarga — reprocesse assim que possível.",
  },
};

/** Origem do disparo (spec 027, RN07). O valor também é o filtro da listagem (spec 036). */
export enum PlanReloadOrigin {
  Manual = "adm-api",
  Automatic = "pln-crn",
}

export const originLabel = (origin?: string): string => {
  if (origin === PlanReloadOrigin.Manual) return "Manual";
  if (origin === PlanReloadOrigin.Automatic) return "Automático";
  return origin ?? "-";
};

export const ORIGIN_OPTIONS = [
  { label: "Todas", value: "" },
  { label: "Manual", value: PlanReloadOrigin.Manual },
  { label: "Automático", value: PlanReloadOrigin.Automatic },
];

export const PROCESS_STATUS_OPTIONS = [
  { label: "Todos", value: "" },
  { label: "Enviado", value: "enviado" },
  { label: "Executando", value: "executando" },
  { label: "Concluído", value: "finalizado" },
];

/**
 * Token de cor por tom. Fica aqui — e não espalhado em `style` — para que nenhuma tela volte a
 * escrever hex (RN02). O consumo é via `var()`, resolvido pelo `ThemeContext` nos dois temas.
 */
export const TONE_COLOR: Record<PlanReloadTone, string> = {
  running: "var(--ant-status-info-fg)",
  success: "var(--ant-status-success-fg)",
  warning: "var(--ant-status-warning-fg)",
  error: "var(--ant-status-error-fg)",
};
